import React, { useMemo } from "react"
import { Box, createStyles, forwardRef, View } from "@design-system/core"

import { SkeletonCircleProps, SkeletonProps, SkeletonTextProps } from "./types"

export const Skeleton = forwardRef<SkeletonProps, "div">((props, ref) => {
  const { style, variant = "rect", width = 100, height = 100 } = props

  return (
    <View
      style={[styles.root, styles[variant], styles.wave, style]}
      width={width}
      height={height}
    >
      <View style={styles.waveAnimation} />
    </View>
  )
})

export const SkeletonCircle = forwardRef<SkeletonCircleProps, "div">(
  (props, ref) => {
    return <Skeleton {...props} variant="circle" />
  },
)

export const SkeletonText = forwardRef<SkeletonTextProps, "div">(
  (props, ref) => {
    const { noOfLines = 3, height = 20, ...rest } = props

    const lines = useMemo(() => {
      // eslint-disable-next-line prefer-spread
      return Array.apply(null, Array(noOfLines)).map((_, i) => ({
        index: i,
        width: i !== noOfLines - 1 ? "100%" : "80%",
      }))
    }, [noOfLines])

    return (
      <Box {...rest}>
        {lines.map((line) => (
          <Skeleton
            key={`skeleton_line_${line.index}`}
            variant="text"
            width={line.width}
            height={height}
          />
        ))}
      </Box>
    )
  },
)

const styles = createStyles((theme) => ({
  root: {
    height: 20,
    backgroundColor: theme.borderColors.default,
  },
  text: {
    marginTop: theme.spaces.spacing4,
    marginBottom: theme.spaces.spacing4,
    borderRadius: theme.borderRadii.small,
  },
  rect: {
    borderRadius: theme.borderRadii.small,
  },
  circle: {
    borderRadius: theme.borderRadii.round,
  },
  wave: {
    overflow: "hidden",
  },
  waveAnimation: {
    backgroundImage:
      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)",
    position: "absolute",
    transform: [{ translateX: "-100%" }],
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    zIndex: 1,
    animationDuration: "1.6s",
    animationDelay: "0.5s",
    animationKeyframes: [
      {
        "0%": {
          transform: "translateX(-100%)",
        },
        "60%": {
          transform: "translateX(100%)",
        },
        "100%": {
          transform: "translateX(100%)",
        },
      },
    ],
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
}))
